import { useCallback, useEffect, useState } from "react";
import { Grid, IconButton, Button, Chip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useIntl } from "react-intl";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import { MacTextField } from "../../MaskedTextField";
import useEditedStyles from "../../../../../hooks/useEditedStyles";
import { SmartalarmUser } from "../types";
import { ResetAdornment, useResetContextMenu } from "../../../../../utils/ResetContextMenuProvider";

export interface MacItemValue {
  uuid: string;
  user: string;
  mac: string;
  deleted?: boolean;
}
interface MacItemProps {
  value: MacItemValue;
  saved: MacItemValue | null;
  users: SmartalarmUser[];
  onChange: (value: MacItemValue) => void;
  disabled?: boolean;
}

const MacItem = ({ value, saved, users, onChange, disabled }: MacItemProps) => {
  const [user, setUser] = useState(value?.user || "");
  const [mac, setMac] = useState(value?.mac || "");

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const { openContextMenu } = useResetContextMenu();

  const onDelete = useCallback(() => {
    value.deleted = true;
    onChange(value);
  }, [value, onChange]);

  const onRestore = useCallback(() => {
    delete value.deleted;
    onChange(value);
  }, [value, onChange]);

  const handleUserChange = useCallback(
    (usr: string) => {
      setUser(usr);
      value.user = usr;

      onChange(value);
    },
    [onChange, value]
  );

  const onReset = useCallback(
    (key: "mac" | "user") => {
      value[key] = saved?.[key] ?? "";
      onChange(value);
    },
    [onChange, saved, value]
  );

  useEffect(() => {
    setUser(value?.user || "");
    setMac(value?.mac || "");
  }, [value]);

  return (
    <Grid item container spacing={1} alignItems="center">
      <Grid item>
        <FormControl
          variant="filled"
          size="small"
          sx={{ margin: 0 }}
          className={saved?.user !== user || users.find((u) => u.uuid === user)?.deleted ? editedStyle.input : ""}
          disabled={disabled || users.filter((u) => !u.deleted).length === 0 || value?.deleted}
          fullWidth
          onContextMenu={
            saved
              ? (e) =>
                  openContextMenu(e, "", {
                    onReset: () => onReset("user"),
                    isEdited: () => saved?.user !== user,
                  })
              : undefined
          }
        >
          <InputLabel id="select-label">{formatMessage({ defaultMessage: "User" })}</InputLabel>
          <Select
            labelId="select-label"
            value={user}
            onChange={(e) => handleUserChange(e.target.value)}
            endAdornment={saved && saved.user !== user ? <ResetAdornment sx={{ mr: 2 }} /> : undefined}
          >
            {users
              .filter((u) => u.uuid === user || !u.deleted)
              .map((option) => (
                <MenuItem key={`option-${option.uuid}`} value={option.uuid} disabled={option.deleted}>
                  {[option.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |` : "", option.code, option.name]
                    .filter((i) => !!i)
                    .join(" ")}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item>
        <MacTextField
          label={formatMessage({ defaultMessage: "MAC address" })}
          value={mac}
          className={saved?.mac !== mac ? editedStyle.input : ""}
          size="small"
          sx={{ margin: 0, marginRight: 1, minWidth: 170, width: 170 }}
          onChange={(v) => setMac(v)}
          onBlur={() => {
            value.mac = mac;
            onChange(value);
          }}
          disabled={disabled || value?.deleted}
          onContextMenu={
            saved && saved.mac !== mac
              ? (e) =>
                  openContextMenu(e, "", {
                    onReset: () => onReset("mac"),
                  })
              : undefined
          }
          InputProps={saved && saved.mac !== mac ? { endAdornment: <ResetAdornment /> } : undefined}
        />
      </Grid>
      {!value?.deleted && !disabled && (
        <Grid item>
          <IconButton sx={{ margin: 0, marginLeft: -1 }} onClick={onDelete}>
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Grid>
      )}
      {value?.deleted && !disabled && (
        <>
          <Grid item>
            <Chip label={formatMessage({ defaultMessage: "deleted" })} size="small" className={editedStyle.chip} />
          </Grid>
          <Grid item>
            <Button size="small" onClick={onRestore} color="inherit">
              {formatMessage({ defaultMessage: "restore" })}
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default MacItem;
